import type Highcharts from "highcharts";
import React from "react";
import useObserverCallback from "../../lib/hooks/useObserverCallback";
import { FIT_WRAPPER_HEIGHT } from "../ui/Wrapper";

declare module "highcharts" {
  interface ChartOptions {
    fitWrapperWidth?: boolean;
    fitWrapperHeight?: boolean;
  }
}

const printMediaWatcher = window.matchMedia("print");

const useWrapperDimensions = (
  wrapperRef: React.RefObject<HTMLElement>,
  chartRef: React.RefObject<Highcharts.Chart>,
) => {
  const resize = React.useCallback(() => {
    const chart = chartRef.current;
    const wrapper = wrapperRef.current;
    if (!chart) throw new Error("Missing chart");
    if (!wrapper) throw new Error("Missing wrapper");
    const fitWrapperWidth = Boolean(chart.options.chart?.fitWrapperWidth);
    const fitWrapperHeight = Boolean(chart.options.chart?.fitWrapperHeight);
    if (!fitWrapperWidth && !fitWrapperHeight) return;

    const currentWidth = chart.container.style.width;
    const currentHeight = chart.container.style.height;
    const rect = wrapper.getBoundingClientRect();
    const width = Math.max(wrapper.clientWidth, rect.width);
    const height = Math.max(wrapper.clientHeight, rect.height);

    const wrapperHeight = `${height}px`;
    const wrapperWidth = `${width}px`;

    const redraw =
      (fitWrapperWidth && wrapperWidth !== currentWidth) ||
      (fitWrapperHeight && wrapperHeight !== currentHeight);

    if (redraw)
      chart.setSize(
        fitWrapperWidth ? width : undefined,
        fitWrapperHeight ? height : undefined,
        false,
      );
  }, [chartRef, wrapperRef]);

  React.useLayoutEffect(() => {
    const chart = chartRef.current;
    const wrapper = wrapperRef.current;
    if (!chart) throw new Error("Missing chart");
    if (!wrapper) throw new Error("Missing wrapper");
    const fitWrapperHeight = Boolean(chart.options.chart?.fitWrapperHeight);
    if (!fitWrapperHeight) return;
    window.requestAnimationFrame(() => {
      wrapper.classList.add(FIT_WRAPPER_HEIGHT);
    });
  }, [chartRef, wrapperRef]);

  // Resize chart when it is printed
  // Chrome only, so far
  // https://bugzilla.mozilla.org/show_bug.cgi?id=774398
  // Also hitting this -- same scenario: I need to resize charts dynamically (using HighCharts) to match the page size. In Chrome I can do this perfectly using window.matchMedia('print'), but this doesn't work in FF. As other posters have indicated, window.onbeforeprint also doesn't work because at this point the document isn't yet sized to the page.
  React.useLayoutEffect(() => {
    printMediaWatcher.addEventListener("change", resize);
    return () => {
      printMediaWatcher.removeEventListener("change", resize);
    };
  }, [resize]);

  // Resize chart when parent change size
  useObserverCallback(resize, [resize], wrapperRef, {
    fireOnInit: true,
    debounced: true,
  });
};

export default useWrapperDimensions;
